<template>
  <div class="min-h-screen py-8">
    <Head :title="`Propietario: ${client.name}`" />

    <!-- Top Navigation -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      <div class="flex items-center space-x-4">
        <button 
          class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          @click="back()"
        >
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver
        </button>
        <h1 class="text-3xl font-extrabold text-gray-900">Ficha del Propietario</h1>
      </div>
    </div>

    <!-- KPI Statistics Cards -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <!-- Total Debt Card -->
        <div class="bg-red-600 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-red-100 truncate">Total Adeudado</dt>
                  <dd class="text-3xl font-bold">${{ parseFloat(client.balance).toFixed(2) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Cuotas Card -->
        <div class="bg-yellow-500 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-yellow-100 truncate">Cuotas Pendientes</dt>
                  <dd class="text-3xl font-bold">{{ orders.length }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <!-- Date Registered Card -->
        <div class="bg-indigo-600 overflow-hidden shadow rounded-lg text-white">
          <div class="p-5">
            <div class="flex items-center">
              <div class="flex-shrink-0 bg-white bg-opacity-20 rounded-md p-3">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div class="ml-5 w-0 flex-1">
                <dl>
                  <dt class="text-sm font-medium text-indigo-100 truncate">Fecha de Registro</dt>
                  <dd class="text-2xl font-bold">{{ formatDate(client.created_at) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        <!-- Left: Owner Profile & Payments Registration Form (Col-span 5) -->
        <div class="lg:col-span-5 space-y-8">
          
          <!-- Owner Details Card -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-150 p-6">
            <h2 class="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center">
              <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Datos del Propietario
            </h2>
            <div class="space-y-3">
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Nombre Completo</span>
                <span class="text-sm font-bold text-gray-900">{{ client.name }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Alias / Cédula</span>
                <span class="text-sm text-gray-900">{{ client.identification }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Apartado / Parcela</span>
                <span class="text-sm text-gray-900">{{ client.companyName || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Calle</span>
                <span class="text-sm text-gray-900">{{ client.street?.name || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Correo electrónico</span>
                <span class="text-sm text-gray-900">{{ client.email || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Teléfono</span>
                <span class="text-sm text-gray-900">{{ client.phone || 'N/A' }}</span>
              </div>
              <div>
                <span class="block text-xs font-semibold text-gray-500 uppercase">Dirección de Vivienda</span>
                <span class="text-sm text-gray-900">{{ client.address || 'N/A' }}</span>
              </div>
            </div>
            <div class="mt-6 pt-4 border-t border-gray-100 space-y-2">
              <a
                :href="`/payments/${client.id}`"
                target="_blank"
                class="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-750 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Ir a Estado de Cuenta Público
              </a>
              <button
                type="button"
                @click="copyPublicLink"
                class="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                {{ copied ? '¡Enlace Copiado!' : 'Copiar Enlace de Consulta' }}
              </button>
              <button
                v-if="isMorochas"
                type="button"
                @click="copyUpdateLink"
                class="w-full inline-flex justify-center items-center py-2 px-4 border border-indigo-200 rounded-md text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <svg class="h-4 w-4 mr-2 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {{ copiedUpdate ? '¡Enlace de Actualización Copiado!' : 'Copiar Enlace Actualizar Datos' }}
              </button>
              <Link
                :href="`/clients/${client.id}/edit`"
                class="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md text-sm font-semibold text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Editar Información
              </Link>
            </div>
          </div>

          <!-- Register Payment Form (Multi-Moneda en Cascadas) -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-150 p-6">
            <h2 class="text-lg font-bold text-gray-900 mb-2 flex items-center">
              <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Registrar Abono / Pago (Multi-Moneda)
            </h2>
            <p class="text-xs text-gray-500 mb-4 leading-relaxed">
              Registra un pago para este propietario. El monto se aplicará automáticamente en forma de **cascada** a las cuotas más antiguas pendientes.
            </p>

            <!-- Tasa del sistema badge -->
            <div class="mb-4 bg-indigo-50 border border-indigo-100 rounded-md p-3 text-xs text-gray-700 flex items-center justify-between">
              <span class="font-medium text-indigo-900">Tasa del sistema actual:</span>
              <span class="font-bold text-indigo-700">1 USD = {{ change }} Bs</span>
            </div>

            <form @submit.prevent="submitPayment">
              <div class="space-y-4">
                <!-- Currency Radio Buttons -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Moneda de Pago</label>
                  <div class="flex items-center space-x-4 mt-1">
                    <label class="inline-flex items-center cursor-pointer">
                      <input 
                        type="radio" 
                        v-model="selectedCurrency" 
                        value="Bs" 
                        class="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500" 
                        @change="onCurrencyChange"
                      />
                      <span class="ml-2 text-sm text-gray-900 font-medium">Bolívares (Bs)</span>
                    </label>
                    <label class="inline-flex items-center cursor-pointer">
                      <input 
                        type="radio" 
                        v-model="selectedCurrency" 
                        value="USD" 
                        class="form-radio h-4 w-4 text-indigo-600 focus:ring-indigo-500" 
                        @change="onCurrencyChange"
                      />
                      <span class="ml-2 text-sm text-gray-900 font-medium">Dólares (USD $)</span>
                    </label>
                  </div>
                </div>

                <!-- Method Select -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
                  <select
                    v-model="paymentForm.method"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    @change="onMethodChange"
                    required
                  >
                    <option value="" disabled>Seleccione método...</option>
                    <option 
                      v-for="pm in filteredPaymentMethods" 
                      :key="pm.id" 
                      :value="pm.name"
                    >
                      {{ pm.name }} ({{ pm.currency ? pm.currency.alias : selectedCurrency }})
                    </option>
                  </select>
                  <p v-if="paymentForm.errors.method" class="text-red-500 text-xs mt-1">{{ paymentForm.errors.method }}</p>
                </div>

                <!-- Amount in Bs -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Monto en Bs.</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="amountBs"
                    :disabled="selectedCurrency !== 'Bs'"
                    @input="onAmountBsInput($event.target.value)"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    placeholder="0.00"
                  />
                  <p v-if="paymentForm.errors.amount_bs" class="text-red-500 text-xs mt-1">{{ paymentForm.errors.amount_bs }}</p>
                </div>

                <!-- Tasa de Cambio del Pago -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ selectedCurrency === 'Bs' ? 'Tasa del Pago (Bs / $)' : 'Tasa Equivalente' }}
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    v-model="paymentForm.change"
                    :disabled="selectedCurrency !== 'Bs'"
                    @input="onChangeInput($event.target.value)"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <p v-if="paymentForm.errors.change" class="text-red-500 text-xs mt-1">{{ paymentForm.errors.change }}</p>
                </div>

                <!-- Amount in USD ($) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ selectedCurrency === 'Bs' ? 'Monto equiv. ($ USD)' : 'Monto ($ USD)' }}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="paymentForm.amount"
                    @input="onAmountUsdInput($event.target.value)"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="0.00"
                    required
                  />
                  <p v-if="paymentForm.errors.amount" class="text-red-500 text-xs mt-1">{{ paymentForm.errors.amount }}</p>
                </div>

                <!-- Reference Number -->
                <div v-if="requiresReference">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Número de Referencia / Comprobante</label>
                  <input
                    type="text"
                    v-model="paymentForm.reference"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Ej: 6057 o 816851760649"
                  />
                  <p v-if="paymentForm.errors.reference" class="text-red-500 text-xs mt-1">{{ paymentForm.errors.reference }}</p>
                </div>

                <div class="pt-2">
                  <button
                    type="submit"
                    :disabled="paymentForm.processing"
                    class="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <svg v-if="paymentForm.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Registrar y Liquidar Pago
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>

        <!-- Right: Pending Cuotas Table (Col-span 7) -->
        <div class="lg:col-span-7">
          <div class="bg-white rounded-lg shadow-sm border border-gray-150 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h2 class="text-lg font-bold text-gray-900 flex items-center">
                <svg class="h-5 w-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Historial de Cuotas por Cobrar (Pendientes)
              </h2>
            </div>
            
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ref Orden</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Descripción</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Abonado</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Faltante</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="order in orders" :key="order.id" class="hover:bg-gray-50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <Link :href="`/orders/${order.id}`" class="text-sm font-semibold text-indigo-600 hover:text-indigo-900">
                        #{{ order.number_order }}
                      </Link>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(order.date) }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {{ order.comentary || 'Cuota de condominio' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ${{ parseFloat(order.total).toFixed(2) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                      ${{ parseFloat(order.balance).toFixed(2) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600 text-right">
                      ${{ (order.total - order.balance).toFixed(2) }}
                    </td>
                  </tr>
                  <tr v-if="orders.length === 0">
                    <td colspan="6" class="px-6 py-8 text-center text-sm text-gray-500 bg-gray-50/50">
                      ¡Felicidades! Este propietario se encuentra al día con sus cuotas y no posee deudas pendientes.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    client: Object,
    orders: Array,
    change: {
      type: Number,
      default: 1
    },
    paymentMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedCurrency: 'Bs',
      amountBs: 0,
      paymentForm: this.$inertia.form({
        amount: 0,
        amount_bs: 0,
        change: this.change,
        method: '',
        reference: ''
      }),
      copied: false,
      copiedUpdate: false
    }
  },
  computed: {
    filteredPaymentMethods() {
      if (!this.paymentMethods || this.paymentMethods.length === 0) {
        if (this.selectedCurrency === 'Bs') {
          return [
            { id: 1, name: 'Pago móvil', currency: { alias: 'Bs' } },
            { id: 2, name: 'Transferencia', currency: { alias: 'Bs' } },
            { id: 3, name: 'Efectivo', currency: { alias: 'Bs' } }
          ]
        } else {
          return [
            { id: 4, name: 'Zelle', currency: { alias: 'USD' } },
            { id: 5, name: 'Efectivo', currency: { alias: 'USD' } },
            { id: 6, name: 'Binance', currency: { alias: 'USD' } }
          ]
        }
      }

      return this.paymentMethods.filter(pm => {
        const alias = pm.currency ? pm.currency.alias : ''
        return alias.toUpperCase() === this.selectedCurrency.toUpperCase()
      })
    },
    requiresReference() {
      const m = (this.paymentForm.method || '').toLowerCase()
      return m.includes('pago móvil') || m.includes('pago movil') || m.includes('transferencia') || m.includes('zelle') || m.includes('binance')
    },
    isMorochas() {
      const acc = this.$page?.props?.auth?.user?.account;
      if (!acc) return false;
      const name = ((acc.name || '') + ' ' + (acc.reasonName || '')).toLowerCase();
      return name.includes('morocha') || acc.id === 1;
    }
  },
  methods: {
    back() {
      window.history.back();
    },
    onCurrencyChange() {
      this.paymentForm.method = ''
      this.paymentForm.amount = 0
      this.paymentForm.amount_bs = 0
      this.paymentForm.change = this.change
      this.amountBs = 0
      this.paymentForm.reference = ''
    },
    onMethodChange() {
      this.paymentForm.reference = ''
    },
    onAmountBsInput(val) {
      const valBs = parseFloat(val) || 0
      this.amountBs = valBs
      this.paymentForm.amount_bs = valBs
      const currentRate = parseFloat(this.paymentForm.change) || this.change || 1
      this.paymentForm.amount = currentRate > 0 ? Number((valBs / currentRate).toFixed(2)) : 0
    },
    onChangeInput(val) {
      const currentRate = parseFloat(val) || 0
      this.paymentForm.change = currentRate
      if (this.selectedCurrency === 'Bs') {
        const valBs = parseFloat(this.amountBs) || 0
        this.paymentForm.amount = currentRate > 0 ? Number((valBs / currentRate).toFixed(2)) : 0
      } else {
        const valUsd = parseFloat(this.paymentForm.amount) || 0
        this.paymentForm.amount_bs = Number((valUsd * currentRate).toFixed(2))
        this.amountBs = this.paymentForm.amount_bs
      }
    },
    onAmountUsdInput(val) {
      const valUsd = parseFloat(val) || 0
      this.paymentForm.amount = valUsd
      const currentRate = parseFloat(this.paymentForm.change) || this.change || 1
      this.paymentForm.amount_bs = Number((valUsd * currentRate).toFixed(2))
      this.amountBs = this.paymentForm.amount_bs
    },
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const cleanDate = dateString.replace('T', ' ').split(' ')[0];
        const parts = cleanDate.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    },
    copyPublicLink() {
      const publicLink = `${window.location.origin}/payments/${this.client.id}`;
      
      const textArea = document.createElement("textarea");
      textArea.value = publicLink;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }
      
      document.body.removeChild(textArea);
    },
    copyUpdateLink() {
      const updateLink = `${window.location.origin}/actualizar-datos?client_id=${this.client.id}`;
      
      const textArea = document.createElement("textarea");
      textArea.value = updateLink;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.copiedUpdate = true;
        setTimeout(() => {
          this.copiedUpdate = false;
        }, 2000);
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }
      
      document.body.removeChild(textArea);
    },
    submitPayment() {
      if (!this.paymentForm.amount || parseFloat(this.paymentForm.amount) <= 0) {
        alert('Por favor, introduzca un monto válido superior a cero.');
        return;
      }
      if (!this.paymentForm.method) {
        alert('Por favor, seleccione un método de pago.');
        return;
      }
      if (this.requiresReference && !this.paymentForm.reference) {
        alert('Debe ingresar el número de referencia para este método de pago.');
        return;
      }
      
      const message = `¿Confirmar cobro de $${parseFloat(this.paymentForm.amount).toFixed(2)} (${this.amountBs ? 'Bs. ' + this.amountBs : 'USD'}) para liquidar las cuotas de ${this.client.name}?`;
      if (confirm(message)) {
        this.paymentForm.post(`/clients/${this.client.id}/pay`, {
          onSuccess: () => {
            this.amountBs = 0;
            this.paymentForm.reset();
            this.paymentForm.change = this.change;
          }
        });
      }
    }
  }
}
</script>
